import { useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useCategory, useCategoryQuestions } from "@/hooks/useExam";
import { categoriesApi } from "@/api/categories";
import QuestionCard from "@/components/exam/QuestionCard";

type ExamPhase = "ready" | "in_progress" | "submitted";

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: category, isLoading: catLoading } = useCategory(slug || "");

  const [phase, setPhase] = useState<ExamPhase>("ready");
  const [activePage, setActivePage] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const startTimeRef = useRef<number>(0);

  // page 1 always fetched to know total_pages for the set list
  const { data: page1Data, isLoading: page1Loading } = useCategoryQuestions(slug || "", 1, 10);
  // active page fetched when user picks a set (same query when activePage===1)
  const { data: examData, isLoading: examLoading } = useCategoryQuestions(
    slug || "",
    activePage,
    10,
  );

  const totalSets = page1Data?.total_pages ?? 0;
  const questions = examData?.items ?? [];
  const totalQuestions = questions.length;

  const handlePickSet = (setNum: number) => {
    setActivePage(setNum);
    setAnswers({});
    setCurrentIndex(0);
    startTimeRef.current = Date.now();
    setPhase("in_progress");
  };

  const handleSubmit = () => {
    const timeTaken = Math.round((Date.now() - startTimeRef.current) / 1000);
    const s = questions.reduce((acc, q) => {
      const selected = answers[q.id];
      const correct = q.answers.find((a) => a.is_correct)?.id;
      return acc + (selected === correct ? 1 : 0);
    }, 0);
    const p = totalQuestions > 0 && s >= Math.ceil(totalQuestions * 0.86);
    if (slug) {
      categoriesApi.recordAttempt(slug, {
        score: s,
        total_questions: totalQuestions,
        passed: p,
        time_taken_seconds: timeTaken,
      }).catch(() => {});
    }
    setPhase("submitted");
    setCurrentIndex(0);
  };

  const handleRetry = () => {
    setAnswers({});
    setCurrentIndex(0);
    setPhase("in_progress");
  };

  const handleBackToSets = () => {
    setAnswers({});
    setCurrentIndex(0);
    setPhase("ready");
  };

  const answeredCount = Object.keys(answers).length;

  const score =
    phase === "submitted"
      ? questions.reduce((acc, q) => {
          const selected = answers[q.id];
          const correct = q.answers.find((a) => a.is_correct)?.id;
          return acc + (selected === correct ? 1 : 0);
        }, 0)
      : 0;

  const passed = totalQuestions > 0 && score >= Math.ceil(totalQuestions * 0.86);

  if (catLoading || page1Loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="page-enter mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        {phase === "ready" ? (
          <Link to="/categories" className="text-sm text-primary-600 hover:text-primary-500 font-medium">
            &larr; All Categories
          </Link>
        ) : (
          <button
            onClick={handleBackToSets}
            className="text-sm text-primary-600 hover:text-primary-500 font-medium"
          >
            &larr; All Practice Sets
          </button>
        )}
      </div>

      {category && (
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{category.name}</h1>
          {category.description && (
            <p className="mt-2 text-gray-500">{category.description}</p>
          )}
          <p className="mt-1 text-sm text-gray-400">
            {category.question_count} questions &middot; {totalSets} practice set{totalSets !== 1 ? "s" : ""}
          </p>
        </div>
      )}

      {/* READY — set picker */}
      {phase === "ready" && (
        <>
          {totalSets === 0 ? (
            <div className="text-center py-20 text-gray-500">No questions available yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {Array.from({ length: totalSets }, (_, i) => i + 1).map((setNum) => {
                const startQ = (setNum - 1) * 10 + 1;
                const endQ = Math.min(setNum * 10, page1Data?.total ?? setNum * 10);
                return (
                  <button
                    key={setNum}
                    onClick={() => handlePickSet(setNum)}
                    className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-primary-300 text-left transition-all group"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 mb-3 group-hover:bg-primary-100 transition-colors">
                      <span className="text-lg font-bold">{setNum}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      Practice {setNum}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Q{startQ}–{endQ}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* IN PROGRESS */}
      {phase === "in_progress" && (
        <>
          {examLoading || !currentQuestion ? (
            <div className="flex items-center justify-center py-20">
              <svg className="animate-spin h-8 w-8 text-primary-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6 sticky top-0 bg-gray-50 py-3 z-10 border-b border-gray-200">
                <span className="text-sm font-medium text-gray-600">
                  Practice {activePage} &middot; {answeredCount} / {totalQuestions} answered
                </span>
                <button
                  onClick={() => {
                    if (
                      answeredCount < totalQuestions &&
                      !window.confirm(
                        `You have ${totalQuestions - answeredCount} unanswered question(s). Submit anyway?`,
                      )
                    )
                      return;
                    handleSubmit();
                  }}
                  className="rounded-lg bg-success-600 px-5 py-2 text-sm font-semibold text-white hover:bg-success-700 transition-colors"
                >
                  Submit
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <QuestionCard
                    question={currentQuestion}
                    questionNumber={currentIndex + 1}
                    totalQuestions={totalQuestions}
                    selectedAnswerId={answers[currentQuestion.id]}
                    onSelectAnswer={(answerId) =>
                      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: answerId }))
                    }
                  />

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                      disabled={currentIndex === 0}
                      className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span>Previous</span>
                    </button>
                    <span className="text-sm text-gray-500">
                      {currentIndex + 1} / {totalQuestions}
                    </span>
                    <button
                      onClick={() => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))}
                      disabled={currentIndex === totalQuestions - 1}
                      className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <span>Next</span>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sticky top-20">
                    <h3 className="text-sm font-semibold text-gray-900 mb-4">Questions</h3>
                    <div className="grid grid-cols-5 gap-2">
                      {questions.map((q, i) => (
                        <button
                          key={q.id}
                          onClick={() => setCurrentIndex(i)}
                          className={[
                            "h-9 w-9 rounded-lg text-sm font-medium transition-colors",
                            i === currentIndex
                              ? "bg-primary-600 text-white"
                              : answers[q.id]
                              ? "bg-primary-100 text-primary-700 border border-primary-300"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                          ].join(" ")}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* SUBMITTED */}
      {phase === "submitted" && (
        <>
          <div
            className={[
              "rounded-xl border p-6 mb-8 text-center",
              passed ? "bg-success-50 border-success-200" : "bg-danger-50 border-danger-200",
            ].join(" ")}
          >
            <div
              className={[
                "text-5xl font-bold mb-2",
                passed ? "text-success-700" : "text-danger-700",
              ].join(" ")}
            >
              {score} / {totalQuestions}
            </div>
            <p className={["text-lg font-semibold", passed ? "text-success-700" : "text-danger-700"].join(" ")}>
              {passed ? "Passed!" : "Not passed"} &mdash; Practice {activePage}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {Math.round((score / totalQuestions) * 100)}% correct
            </p>
            <div className="mt-4 flex justify-center gap-3 flex-wrap">
              <button
                onClick={handleRetry}
                className="rounded-lg bg-primary-600 px-6 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToSets}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                All Practice Sets
              </button>
            </div>
          </div>

          {currentQuestion && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <QuestionCard
                  question={currentQuestion}
                  questionNumber={currentIndex + 1}
                  totalQuestions={totalQuestions}
                  selectedAnswerId={answers[currentQuestion.id]}
                  showResult
                  onSelectAnswer={() => {}}
                />

                <div className="mt-6 flex items-center justify-between">
                  <button
                    onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                    disabled={currentIndex === 0}
                    className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Previous</span>
                  </button>
                  <span className="text-sm text-gray-500">
                    {currentIndex + 1} / {totalQuestions}
                  </span>
                  <button
                    onClick={() => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))}
                    disabled={currentIndex === totalQuestions - 1}
                    className="flex items-center space-x-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span>Next</span>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm sticky top-20">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Review</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {questions.map((q, i) => {
                      const selected = answers[q.id];
                      const correct = q.answers.find((a) => a.is_correct)?.id;
                      const isCorrect = selected === correct;
                      const unanswered = !selected;
                      return (
                        <button
                          key={q.id}
                          onClick={() => setCurrentIndex(i)}
                          className={[
                            "h-9 w-9 rounded-lg text-sm font-medium transition-colors",
                            i === currentIndex ? "ring-2 ring-offset-1 ring-gray-400" : "",
                            unanswered
                              ? "bg-gray-100 text-gray-500"
                              : isCorrect
                              ? "bg-success-100 text-success-700 border border-success-300"
                              : "bg-danger-100 text-danger-700 border border-danger-300",
                          ].join(" ")}
                        >
                          {i + 1}
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-4 space-y-1 text-xs text-gray-500">
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm bg-success-100 border border-success-300 inline-block" />
                      Correct
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm bg-danger-100 border border-danger-300 inline-block" />
                      Incorrect
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
